import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AssistantAction, SmartVideoDraftPayload } from '../../../core/services/voice-actions/models';
import {
  readActionPayloadValue,
  readBooleanPayload,
  readNumberPayload
} from '../../../core/services/voice-actions/voice-action-payload';
import { NichePreset } from '../models/video-generator.models';
import { VideoGeneratorService } from './video-generator.service';
import { resolveSmartVideoDraftSource } from './smart-video-draft-lifecycle';

const SMART_VIDEO_DRAFT_KEY = 'voice-actions:smart-video:draft';

@Injectable({
  providedIn: 'root'
})
export class SmartVideoVoiceActionService {
  private readonly router = inject(Router);
  private readonly videoService = inject(VideoGeneratorService);

  async openCreate(action: AssistantAction): Promise<void> {
    this.persistDraft(await this.extractDraft(action));
    await this.router.navigate(['/video-gen', 'generator'], {
      queryParams: { from: 'voice-assistant' }
    });
  }

  private async extractDraft(action: AssistantAction): Promise<SmartVideoDraftPayload | null> {
    const inputType = readActionPayloadValue<'Topic' | 'Script' | 'Style'>(action, 'inputType');
    const inputData = readActionPayloadValue<string>(action, 'inputData');

    const draft: SmartVideoDraftPayload = {
      source: resolveSmartVideoDraftSource(action),
      inputType: inputType || 'Topic'
    };

    if (inputData?.trim()) {
      draft.inputData = inputData.trim();
    }

    const nichePresetId = readActionPayloadValue<string>(action, 'nichePresetId');
    if (nichePresetId?.trim()) {
      draft.nichePresetId = nichePresetId.trim();
    } else {
      const matchedPresetId = await this.resolveNichePresetId(readActionPayloadValue<string>(action, 'nicheQuery'));
      if (matchedPresetId) {
        draft.nichePresetId = matchedPresetId;
      }
    }

    const autoReview = readBooleanPayload(action, 'autoReview');
    if (autoReview !== undefined) draft.autoReview = autoReview;

    const durationSeconds = readNumberPayload(action, 'durationSeconds');
    if (durationSeconds !== undefined) draft.durationSeconds = durationSeconds;

    const sceneCount = readNumberPayload(action, 'sceneCount');
    if (sceneCount !== undefined) draft.sceneCount = sceneCount;

    const aiImageCount = readNumberPayload(action, 'aiImageCount');
    if (aiImageCount !== undefined) draft.aiImageCount = aiImageCount;

    const aiVideoCount = readNumberPayload(action, 'aiVideoCount');
    if (aiVideoCount !== undefined) draft.aiVideoCount = aiVideoCount;

    const timelineBlocks = this.readTimelineBlocksPayload(action);
    if (timelineBlocks?.length) draft.timelineBlocks = timelineBlocks;

    const featuredCharacterIds = this.readStringArrayPayload(action, 'featuredCharacterIds');
    if (featuredCharacterIds?.length) draft.featuredCharacterIds = featuredCharacterIds;

    const featuredEnvironmentIds = this.readStringArrayPayload(action, 'featuredEnvironmentIds');
    if (featuredEnvironmentIds?.length) draft.featuredEnvironmentIds = featuredEnvironmentIds;

    const featuredProductIds = this.readStringArrayPayload(action, 'featuredProductIds');
    if (featuredProductIds?.length) draft.featuredProductIds = featuredProductIds;

    const enableCharacterDialogue = readBooleanPayload(action, 'enableCharacterDialogue');
    if (enableCharacterDialogue !== undefined) draft.enableCharacterDialogue = enableCharacterDialogue;

    const dialogueTtsMode = readActionPayloadValue<string>(action, 'dialogueTtsMode');
    if (dialogueTtsMode === 'NarratorOnly' || dialogueTtsMode === 'MultiVoice') {
      draft.dialogueTtsMode = dialogueTtsMode;
    }

    const scriptSystemPrompt = readActionPayloadValue<string>(action, 'scriptSystemPrompt');
    if (scriptSystemPrompt?.trim()) draft.scriptSystemPrompt = scriptSystemPrompt.trim();

    const scriptTemperature = readNumberPayload(action, 'scriptTemperature');
    if (scriptTemperature !== undefined) draft.scriptTemperature = scriptTemperature;

    const imageStyle = readActionPayloadValue<string>(action, 'imageStyle');
    if (imageStyle?.trim()) draft.imageStyle = imageStyle.trim();

    const imageStylePromptEnhancement = readActionPayloadValue<string>(action, 'imageStylePromptEnhancement');
    if (imageStylePromptEnhancement !== undefined) draft.imageStylePromptEnhancement = imageStylePromptEnhancement;

    const aspectRatio = readActionPayloadValue<string>(action, 'aspectRatio');
    if (aspectRatio) {
      draft.aspectRatio = aspectRatio;
    }

    const imageResolution = readActionPayloadValue<string>(action, 'imageResolution');
    if (imageResolution) draft.imageResolution = imageResolution;

    const imageAspectRatio = readActionPayloadValue<string>(action, 'imageAspectRatio');
    if (imageAspectRatio) draft.imageAspectRatio = imageAspectRatio;

    const imageQuality = readActionPayloadValue<string>(action, 'imageQuality');
    if (imageQuality) draft.imageQuality = imageQuality;

    const imageOutputFormat = readActionPayloadValue<string>(action, 'imageOutputFormat');
    if (imageOutputFormat) draft.imageOutputFormat = imageOutputFormat;

    const generateThumbnails = readBooleanPayload(action, 'generateThumbnails');
    if (generateThumbnails !== undefined) {
      draft.generateThumbnails = generateThumbnails;
    }

    const generateShorts = readBooleanPayload(action, 'generateShorts');
    if (generateShorts !== undefined) {
      draft.generateShorts = generateShorts;
    }

    const shortsCount = readNumberPayload(action, 'shortsCount');
    if (shortsCount !== undefined) {
      draft.shortsCount = shortsCount;
    }

    const selectedSceneIndexes = readActionPayloadValue<unknown>(action, 'selectedSceneIndexes');
    if (Array.isArray(selectedSceneIndexes)) {
      const normalized = selectedSceneIndexes
        .map((value) => Number(value))
        .filter(value => Number.isInteger(value) && value >= 0);
      if (normalized.length) {
        draft.selectedSceneIndexes = normalized;
      }
    }

    const useTts = readBooleanPayload(action, 'useTts');
    if (useTts !== undefined) draft.useTts = useTts;

    const useBrowserTts = readBooleanPayload(action, 'useBrowserTts');
    if (useBrowserTts !== undefined) draft.useBrowserTts = useBrowserTts;

    const browserTtsVoiceUri = readActionPayloadValue<string>(action, 'browserTtsVoiceUri');
    if (browserTtsVoiceUri) draft.browserTtsVoiceUri = browserTtsVoiceUri;

    const ttsVoiceId = readActionPayloadValue<string>(action, 'ttsVoiceId');
    if (ttsVoiceId !== undefined) draft.ttsVoiceId = ttsVoiceId;

    const useAiVideoGeneration = readBooleanPayload(action, 'useAiVideoGeneration');
    if (useAiVideoGeneration !== undefined) draft.useAiVideoGeneration = useAiVideoGeneration;

    const aiVideoMode = readActionPayloadValue<string>(action, 'aiVideoMode');
    if (aiVideoMode === 'TextToVideo' || aiVideoMode === 'ImageToVideo') draft.aiVideoMode = aiVideoMode;

    const aiVideoIntensity = readActionPayloadValue<string>(action, 'aiVideoIntensity');
    if (aiVideoIntensity === 'AllScenes' || aiVideoIntensity === 'KeyScenesOnly' || aiVideoIntensity === 'EveryNthScene') {
      draft.aiVideoIntensity = aiVideoIntensity;
    }

    const videoResolution = readActionPayloadValue<string>(action, 'videoResolution');
    if (videoResolution) draft.videoResolution = videoResolution;

    const videoAspectRatio = readActionPayloadValue<string>(action, 'videoAspectRatio');
    if (videoAspectRatio) draft.videoAspectRatio = videoAspectRatio;

    const videoQuality = readActionPayloadValue<string>(action, 'videoQuality');
    if (videoQuality) draft.videoQuality = videoQuality;

    const videoDurationSeconds = readNumberPayload(action, 'videoDurationSeconds');
    if (videoDurationSeconds !== undefined) draft.videoDurationSeconds = videoDurationSeconds;

    const videoOutputFormat = readActionPayloadValue<string>(action, 'videoOutputFormat');
    if (videoOutputFormat) draft.videoOutputFormat = videoOutputFormat;

    const scriptProvider = readActionPayloadValue<string>(action, 'scriptProvider');
    if (scriptProvider !== undefined) draft.scriptProvider = scriptProvider;
    const scriptModel = readActionPayloadValue<string>(action, 'scriptModel');
    if (scriptModel !== undefined) draft.scriptModel = scriptModel;
    const imageProvider = readActionPayloadValue<string>(action, 'imageProvider');
    if (imageProvider !== undefined) draft.imageProvider = imageProvider;
    const imageModel = readActionPayloadValue<string>(action, 'imageModel');
    if (imageModel !== undefined) draft.imageModel = imageModel;
    const ttsProvider = readActionPayloadValue<string>(action, 'ttsProvider');
    if (ttsProvider !== undefined) draft.ttsProvider = ttsProvider;
    const ttsModel = readActionPayloadValue<string>(action, 'ttsModel');
    if (ttsModel !== undefined) draft.ttsModel = ttsModel;
    const videoProvider = readActionPayloadValue<string>(action, 'videoProvider');
    if (videoProvider !== undefined) draft.videoProvider = videoProvider;
    const videoModel = readActionPayloadValue<string>(action, 'videoModel');
    if (videoModel !== undefined) draft.videoModel = videoModel;

    const renderOutput = readActionPayloadValue<SmartVideoDraftPayload['renderOutput']>(action, 'renderOutput');
    if (renderOutput && typeof renderOutput === 'object') draft.renderOutput = renderOutput;

    return Object.keys(draft).length > 2 ? draft : null;
  }

  private readStringArrayPayload(action: AssistantAction, key: string): string[] | undefined {
    const value = readActionPayloadValue<unknown>(action, key);
    if (!Array.isArray(value)) return undefined;

    const normalized = value
      .map(item => typeof item === 'string' ? item.trim() : '')
      .filter(Boolean);

    return normalized.length ? Array.from(new Set(normalized)) : undefined;
  }

  private readTimelineBlocksPayload(action: AssistantAction): SmartVideoDraftPayload['timelineBlocks'] | undefined {
    const value = readActionPayloadValue<unknown>(action, 'timelineBlocks');
    if (!Array.isArray(value)) return undefined;

    const normalized = value
      .map(item => {
        const candidate = item as { kind?: unknown; durationSeconds?: unknown } | null | undefined;
        const kind = candidate?.kind === 'video' ? 'video' : candidate?.kind === 'image' ? 'image' : null;
        const durationSeconds = Number(candidate?.durationSeconds);
        return kind && Number.isFinite(durationSeconds)
          ? { kind, durationSeconds: Math.trunc(durationSeconds) }
          : null;
      })
      .filter((item): item is { kind: 'image' | 'video'; durationSeconds: number } =>
        !!item && item.durationSeconds > 0);

    return normalized.length ? normalized : undefined;
  }

  private persistDraft(payload: SmartVideoDraftPayload | null): void {
    if (!payload) return;

    const draft = {
      ...payload,
      createdAt: new Date().toISOString()
    };

    try {
      sessionStorage.setItem(SMART_VIDEO_DRAFT_KEY, JSON.stringify(draft));
      localStorage.setItem(SMART_VIDEO_DRAFT_KEY, JSON.stringify(draft));
    } catch (error) {
      console.warn('Unable to persist Smart Video draft from voice action:', error);
    }
  }

  private async resolveNichePresetId(nicheQuery?: string): Promise<string | undefined> {
    const normalizedQuery = this.normalizeSearchText(nicheQuery);
    if (!normalizedQuery) return undefined;

    try {
      const presets = await firstValueFrom(this.videoService.getPresets());
      return this.bestNichePreset((presets || []).filter((preset) => preset.isActive !== false), normalizedQuery)?.id;
    } catch (error) {
      console.warn('Unable to resolve niche preset from voice action:', error);
      return undefined;
    }
  }

  private bestNichePreset(presets: NichePreset[], normalizedQuery: string): NichePreset | undefined {
    let best: { preset: NichePreset; score: number } | undefined;

    for (const preset of presets) {
      const candidate = this.normalizeSearchText(`${preset.name} ${preset.nicheType} ${preset.description || ''}`);
      const score = this.searchScore(normalizedQuery, candidate);
      if (!best || score > best.score) {
        best = { preset, score };
      }
    }

    return best && best.score >= 0.38 ? best.preset : undefined;
  }

  private searchScore(query: string, candidate: string): number {
    if (!query || !candidate) return 0;
    if (candidate.includes(query) || query.includes(candidate)) return 1;

    const queryTokens = query.split(' ').filter(Boolean);
    if (!queryTokens.length) return 0;

    const tokenHits = queryTokens.filter((token) =>
      token.length >= 4 && candidate.includes(token.slice(0, Math.min(token.length, 6)))
    ).length;

    return tokenHits / queryTokens.length;
  }

  private normalizeSearchText(value?: string): string {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9\u0600-\u06ff]+/g, ' ')
      .replace(/\b(style|video|smart|niche|please|open|create|banao|kholo)\b/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
}
